/**
 * Utility functions for determining order types based on price comparison
 */

export type OrderType = 'limit' | 'stop';

export interface OrderTypeDetection {
  orderType: OrderType;
  description: string;
  isStopOrder: boolean;
}

/**
 * Determines the order type for a buy order based on input price vs current price
 * @param inputPrice - The price entered by the user
 * @param currentPrice - The current market price
 * @returns OrderTypeDetection object with order type and description
 */
export const detectBuyOrderType = (inputPrice: number, currentPrice: number): OrderTypeDetection => {
  if (inputPrice < currentPrice) {
    return {
      orderType: 'limit',
      description: 'Buy Limit Order',
      isStopOrder: false
    };
  } else {
    return {
      orderType: 'stop',
      description: 'Buy Stop Order',
      isStopOrder: true
    };
  }
};

/**
 * Determines the order type for a sell order based on input price vs current price
 * @param inputPrice - The price entered by the user
 * @param currentPrice - The current market price
 * @returns OrderTypeDetection object with order type and description
 */
export const detectSellOrderType = (inputPrice: number, currentPrice: number): OrderTypeDetection => {
  if (inputPrice > currentPrice) {
    return {
      orderType: 'limit',
      description: 'Sell Limit Order',
      isStopOrder: false
    };
  } else {
    return {
      orderType: 'stop',
      description: 'Sell Stop Order',
      isStopOrder: true
    };
  }
};

/**
 * Gets the appropriate order type for any order based on side and price comparison
 * @param side - 'buy' or 'sell'
 * @param inputPrice - The price entered by the user
 * @param currentPrice - The current market price
 * @returns OrderTypeDetection object with order type and description
 */
export const detectOrderType = (side: 'buy' | 'sell', inputPrice: number, currentPrice: number): OrderTypeDetection => {
  if (side === 'buy') {
    return detectBuyOrderType(inputPrice, currentPrice);
  } else {
    return detectSellOrderType(inputPrice, currentPrice);
  }
};
